import React, { useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import axios from "axios";
import { useSelector } from "react-redux";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import { API_ENDPOINT } from "../../../services/api/resources";
import {
  DOCUMENT_UPLOAD_ACCEPT,
  DOCUMENT_UPLOAD_FORMAT_LABEL,
  isDocumentUploadFile,
} from "../../../utils/documentUploadTypes";
import {
  getMatchedCatalogItems,
  normalizePriceCatalogItems,
} from "./priceListCatalogMapping";

const getVendorNameFromHeader = (headerInfo = {}) => {
  const vendorDetails = String(headerInfo?.vendor_details || "").trim();
  if (!vendorDetails) {
    return "-";
  }

  return vendorDetails.split("\n")[0]?.trim() || "-";
};

const normalizeCatalogExtractionResponse = (payload, file) => {
  const root =
    payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data)
      ? payload.data
      : payload || {};

  const headerInfo = root?.header_info || {};
  const companyDetails = root?.company_details || {};
  const normalizedItems = normalizePriceCatalogItems(getMatchedCatalogItems(payload));

  return {
    ...root,
    header_info: headerInfo,
    company_details: {
      ...companyDetails,
      company_name:
        companyDetails?.company_name ||
        root?.vendor_name ||
        root?.supplier_name ||
        getVendorNameFromHeader(headerInfo),
      gst_number:
        companyDetails?.gst_number ||
        root?.gst_number ||
        headerInfo?.gstin ||
        "-",
    },
    document_number: file?.name || root?.document_number || "Price Catalog",
    buyer_name: root?.buyer_name || "-",
    vendor_name:
      root?.vendor_name ||
      root?.supplier_name ||
      companyDetails?.company_name ||
      getVendorNameFromHeader(headerInfo),
    supplier_name:
      root?.supplier_name ||
      root?.vendor_name ||
      companyDetails?.company_name ||
      getVendorNameFromHeader(headerInfo),
    created_date: root?.created_date || root?.created_at || new Date().toISOString().split("T")[0],
    matched_count: normalizedItems.length,
    line_items: normalizedItems,
    matched_results: {
      ...(root?.matched_results || {}),
      matched_items: normalizedItems,
      grand_total:
        root?.matched_results?.grand_total ||
        root?.totals?.grand_total ||
        root?.grand_total ||
        "0.00",
    },
    raw_filter_response: payload,
  };
};

const PriceCatalogUploadPage = ({ onBack, onSave }) => {
  const loginData = useSelector((state) => state.loginData.value);
  const fileInputRef = useRef(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [extractError, setExtractError] = useState("");

  const authToken =
    loginData?.auth_token ||
    loginData?.access_token ||
    loginData?.token ||
    loginData?.user?.auth_token ||
    "";

  const handleRcDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!isDocumentUploadFile(file)) return;
    setPdfFile(file);
    setExtractedData(null);
    setShowPdfPreview(false);
    setExtractError("");
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!isDocumentUploadFile(file)) return;
    setPdfFile(file);
    setExtractedData(null);
    setShowPdfPreview(false);
    setExtractError("");
  };

  const handleExtract = async () => {
    if (!pdfFile) return;
    try {
      setLoading(true);
      setExtractError("");

      const formData = new FormData();
      formData.append("extract", pdfFile);

      const headers = {
        "Content-Type": "multipart/form-data",
      };

      if (authToken) {
        headers.Authorization = `Token ${authToken}`;
      }

      const response = await axios.post(
        API_ENDPOINT.extract_pdf_no_filter_view,
        formData,
        {
          headers,
        },
      );

      const payload = response?.data || {};

      if (payload?.status === false || payload?.success === false) {
        setExtractError(payload?.message || "Unable to extract price catalog.");
        setExtractedData(null);
        return;
      }

      setExtractedData(
        normalizeCatalogExtractionResponse(payload, pdfFile),
      );
    } catch (error) {
      console.error("Error extracting price catalog:", error);
      setExtractError(
        error?.response?.data?.message ||
          error?.response?.data?.response ||
          "Unable to extract price catalog. Please try again.",
      );
      setExtractedData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (!extractedData) return;
    onSave(extractedData);
  };

  const handleClearFile = () => {
    setPdfFile(null);
    setExtractedData(null);
    setShowPdfPreview(false);
    setExtractError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          mt: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <HeaderTitle titleText="Upload Price Catalog" />
        <Button
          variant="contained"
          size="small"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            textTransform: "none",
            px: 1,
            py: 1,
            borderRadius: 1.5,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }}
          onClick={onBack}
        >
          Back
        </Button>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "space-between" }}>
        <Box
          sx={{
            flex: "1 1 420px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography
            // variant="h5"
            sx={{
              fontFamily:"gilroyBold",
              // fontWeight: 600,
              color: "#1e293b",
              mb: 1,
              fontSize: "18px",
            }}
          >
            Price Catalog 
          </Typography>

          <Box
            onDrop={handleRcDrop}
            onDragOver={handleDragOver}
            sx={{
              width:"500px",
              border: "2px dashed #cbd5e1",
              borderRadius: "12px",
              p: 4,
              textAlign: "center",
              backgroundColor: "#f8fafc",
              cursor: "pointer",
              "&:hover": { backgroundColor: "#f1f5f9" },
              transition: "background-color 0.2s ease",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: 16,
                color: "#334155",
                mb: 0.5,
                wordBreak: "break-all",
              }}
            >
              {pdfFile ? pdfFile.name : "Drag and drop file here"}
            </Typography>

            <Typography variant="body2" sx={{ color: "#64748b", fontSize: 14, mb: 2 }}>
              (Supported: {DOCUMENT_UPLOAD_FORMAT_LABEL})
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
              <Button
                variant="outlined"
                size="medium"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 14,
                  fontWeight: 500,
                  px: 2.5,
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                Browse
              </Button>

              {pdfFile && (
                <>
                  <Button
                    variant="text"
                    color="primary"
                    size="medium"
                    sx={{
                      textTransform: "none",
                      fontSize: 14,
                      fontWeight: 500,
                      px: 2.5,
                    }}
                    onClick={() => setShowPdfPreview((prev) => !prev)}
                  >
                    {showPdfPreview ? "Hide Preview" : "Preview"}
                  </Button>

                  <Button
                    variant="text"
                    color="error"
                    size="medium"
                    sx={{
                      textTransform: "none",
                      fontSize: 14,
                      fontWeight: 500,
                      px: 2.5,
                    }}
                    onClick={handleClearFile}
                  >
                    Clear
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept={DOCUMENT_UPLOAD_ACCEPT}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {showPdfPreview && pdfFile && (
        <Box
          sx={{
            mt: 2,
            p: 1,
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            backgroundColor: "white",
            maxWidth: "720px",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: "#075b97",
              fontWeight: 600,
              mb: 1,
              fontSize: 14,
              textAlign: "left",
            }}
          >
            PDF Preview:
          </Typography>
          <iframe
            src={URL.createObjectURL(pdfFile)}
            title="PDF Preview"
            width="100%"
            height="250px"
            style={{
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
            }}
          />
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
        <Button
          variant="contained"
          size="small"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            fontFamily:"gilroyBold",
            textTransform: "none",
            px: 1,
            py: 1,
            borderRadius: 1.5,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            opacity: !pdfFile || loading ? 0.7 : 1,
          }}
          onClick={handleExtract}
          disabled={!pdfFile || loading}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : "Extract"}
        </Button>

        <Button
          variant="contained"
          size="small"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            fontFamily:"gilroyBold",
            textTransform: "none",
            px: 1,
            py: 1,
            borderRadius: 1.5,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            opacity: !extractedData || loading ? 0.7 : 1,
          }}
          onClick={handleSave}
          disabled={!extractedData || loading}
        >
          Save
        </Button>
      </Box>

      {extractError ? (
        <Typography
          sx={{
            color: "#b91c1c",
            fontSize: 14,
            mt: 1,
          }}
        >
          {extractError}
        </Typography>
      ) : null}
    </Box>
  );
};

export default PriceCatalogUploadPage;
